import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import {
    createOrUpdateBorrower,
    getBorrower,
    getTermsAndConditionsAgreement,
} from '@/apiCalls/borrower';
import {
    EVerificationResponseStatus,
    IVerifyCodeReturn,
    sendEmailCode,
    sendSmsCode,
    verifyEmailCode,
    verifySmsCode,
} from '@/apiCalls/verification';
import { Button, EButtonType } from '@/components/Button';
import { Input } from '@/components/Input';
import {
    EEmploymentStatus,
    ELoanUsage,
    EUserFlow,
    selectAddressByIndex,
    selectEmail,
    selectFirstName,
    selectLoanUsage,
    selectMobile,
    selectVehicle,
} from '@/context/borrower';
import {
    EDisplays,
    EModals,
    selectDisplay,
    selectIsAssetKnownViaParams,
    selectModal,
    selectSignupOptions,
    selectSmsLastRequestTimestamp,
    selectSmsRequestAttempts,
    selectUserFlow,
    setDisplay,
    setModal,
    setSmsLastRequestTimestamp,
    setSmsRequestAttempts,
} from '@/context/display';
import { RootState, store } from '@/context/store';
import { useAppDispatch, useAppSelector } from '@/context/storeHooks';
import {
    EUserLoginOption,
    selectMobileOrEmail,
    setIsLoggedIn,
} from '@/context/user';
import { Email } from '@/UserInput/Email';
import { REACT_APP_ENV } from '@/utils/constants';
import { isEmail } from '@/utils/isEmail';
import { isMobile } from '@/utils/isMobile';
import { EConversionType, reportConversion } from '@/utils/reportConversion';

import { FirstName } from '../FirstName';
import { PostCode } from '../PostCode';

export interface IProps {
    baseId: string;
    showErrors: boolean;
    disabled?: boolean;
}

export const testId = 'LoginCode';

// Todo: Separate the login code and the login button into separate components
export function LoginCode({
    baseId,
    showErrors,
    disabled = false,
}: IProps): JSX.Element {
    // ***** Redux *****
    const currentState = useAppSelector(selectEmail);
    const dispatch = useAppDispatch();
    const loginMethod = useAppSelector(selectMobileOrEmail);
    const mobileNumber = useAppSelector(selectMobile);
    const email = useAppSelector(selectEmail);
    const loanUsage = useAppSelector(selectLoanUsage);
    const display = useAppSelector(selectDisplay);
    const modal = useAppSelector(selectModal);
    const firstName = useAppSelector(selectFirstName);
    const signupOptions = useAppSelector(selectSignupOptions);
    const vehicle = useAppSelector(selectVehicle);
    const isAssetKnownFromParams = useAppSelector(selectIsAssetKnownViaParams);
    const isProduction =
        REACT_APP_ENV === 'production' || REACT_APP_ENV === 'uat';
    const loggingAttempts = isProduction ? 5 : 100; // Allow more attempts in non-production environments
    // ***** Local State *****
    const [code, setCode] = useState<string>('');
    const [invalidCode, setInvalidCode] = useState<boolean>(false);
    const [sending, setSending] = useState<boolean>(false);
    const [sent, setSent] = useState<boolean>(false);
    const [error, setError] = useState<boolean>(false);
    const [checking, setChecking] = useState<boolean>(false);
    const [notFound, setNotFound] = useState<boolean>(false);
    const [rateLimitingError, setRateLimitingError] = useState<boolean>(false);
    const userFlow = useAppSelector(selectUserFlow);
    const noLender =
        userFlow === EUserFlow.NO_LENDER ||
        userFlow === EUserFlow.NO_LENDER_EARLY_SIGNUP;
    const address = useSelector((state: RootState) =>
        selectAddressByIndex(state, 0)
    );
    const { postcode } = address;

    const smsRequestAttempts = useAppSelector(selectSmsRequestAttempts);
    const smsLastRequestTimestamp = useAppSelector(
        selectSmsLastRequestTimestamp
    );
    const customerEmail = useSelector((state: RootState) => selectEmail(state));

    useEffect(() => {
        if (code?.length !== 6) return;
        // todo: dispatch check code
        checkLoginCode();
    }, [code]);

    useEffect(() => {
        // Validate timestamp is a proper Date object when loaded from storage
        if (
            smsLastRequestTimestamp &&
            !(smsLastRequestTimestamp instanceof Date)
        ) {
            // Convert string timestamp to Date if needed
            const timestamp = new Date(smsLastRequestTimestamp);
            if (!isNaN(timestamp.getTime())) {
                dispatch(setSmsLastRequestTimestamp(timestamp));
            } else {
                dispatch(setSmsLastRequestTimestamp(null));
            }
        }
    }, []);

    useEffect(() => {
        if (
            loginMethod === EUserLoginOption.MOBILE &&
            smsRequestAttempts >= loggingAttempts &&
            smsLastRequestTimestamp
        ) {
            const now = Date.now();
            const timeSinceLastRequest =
                now - (smsLastRequestTimestamp?.getTime() || 0);

            if (timeSinceLastRequest < 15 * 60 * 1000) {
                setRateLimitingError(true);
            } else {
                setRateLimitingError(false);
            }
        } else {
            setRateLimitingError(false);
        }
    }, [loginMethod, smsRequestAttempts, smsLastRequestTimestamp]);

    // ***** Event Handlers *****
    function handleChange(value: string | boolean): void {
        setInvalidCode(false);
        setError(false);
        if (typeof value === 'boolean') return;
        if (value === currentState) return;
        if (value?.length > 6) return;
        setCode(value);
    }

    async function handleClick(): Promise<void> {
        // Check rate limiting before sending
        if (
            loginMethod === EUserLoginOption.MOBILE &&
            smsRequestAttempts >= loggingAttempts
        ) {
            const now = Date.now();
            const timeSinceLastRequest =
                now - (smsLastRequestTimestamp?.getTime() || 0);

            if (timeSinceLastRequest < 15 * 60 * 1000) {
                // 15 minutes
                setRateLimitingError(true);
                dispatch(setSmsRequestAttempts(0));

                return; // Don't proceed with sending
            } else {
                // Reset if 15 minutes have passed
                dispatch(setSmsRequestAttempts(0));
                setRateLimitingError(false);
                dispatch(setSmsLastRequestTimestamp(null));
            }
        }

        let code: EVerificationResponseStatus | void = undefined;

        // Only update attempts and timestamp if we're actually sending
        dispatch(setSmsRequestAttempts(smsRequestAttempts + 1));
        dispatch(setSmsLastRequestTimestamp(new Date()));

        if (loginMethod === EUserLoginOption.MOBILE) {
            code = await loginViaMobile();
        } else {
            code = await loginViaEmail();
        }
        useCode(code);
    }

    async function login(): Promise<void> {
        if (smsRequestAttempts > loggingAttempts) {
            dispatch(setSmsRequestAttempts(0));
        }
        dispatch(setIsLoggedIn(true));
        // 1) hydrate store with everything the server has
        await getBorrower();

        // 2) now createOrUpdate will carry the real income/expense/etc
        await createOrUpdateBorrower();
        getTermsAndConditionsAgreement().then(async (termsAndConditions) => {
            // Save/create the borrower

            const state = store.getState();

            const isNovatedLease =
                state.borrowerReducer.loan.loanUsage === ELoanUsage.NOVATED;
            if (
                isNovatedLease &&
                (display === EDisplays.VEHICLE ||
                    display === EDisplays.BASIC ||
                    display === EDisplays.INCOME)
            ) {
                if (state.lenderReducer.lenders?.length === 0) {
                    if (
                        display === EDisplays.VEHICLE ||
                        display === EDisplays.BASIC
                    ) {
                        dispatch(setModal(null));
                        dispatch(setDisplay(EDisplays.INELIGIBLE));
                        return;
                    }
                    const employmentStatus =
                        state.borrowerReducer.employments?.employments[0]
                            .status;
                    if (
                        employmentStatus === EEmploymentStatus.CASUAL &&
                        loanUsage === ELoanUsage.NOVATED
                    ) {
                        dispatch(setModal(null));
                        dispatch(setDisplay(EDisplays.EMPLOYMENTINELIGIBLE));
                        return;
                    } else if (
                        state.lenderReducer.lenders?.length === 0 &&
                        loanUsage === ELoanUsage.NOVATED
                    ) {
                        dispatch(setModal(null));
                        dispatch(setDisplay(EDisplays.INCOMEINELIGIBLE));
                        return;
                    }
                }
            }
            if (noLender && isAssetKnownFromParams) {
                dispatch(setModal(null));
                if (loanUsage === ELoanUsage.BUSINESS) {
                    dispatch(setDisplay(EDisplays.BUSINESS));
                    return;
                } else {
                    dispatch(setDisplay(EDisplays.BASIC));
                    return;
                }
            }

            if (
                loanUsage !== ELoanUsage.BUSINESS &&
                (!termsAndConditions ||
                    !termsAndConditions.isDefaultPrivacyAgreed?.agreed ||
                    !termsAndConditions.isMarketingConsentAgreed?.agreed ||
                    !termsAndConditions.isEquifaxVerificationExchangeAgreed
                        ?.agreed)
            ) {
                dispatch(setModal(EModals.EXPRESS_PRIVACY));
                return;
            }

            if (!state.borrowerReducer.application.isSubmitted) {
                dispatch(setModal(null));
            }

            if (display === EDisplays.SERVICING_SUCCESS) {
                dispatch(setDisplay(EDisplays.IDENTIFICATION));
            }

            if (display === EDisplays.QUICK_CALCULATOR) {
                dispatch(setDisplay(EDisplays.QUICK_CALCULATOR));
            }

            // commercial applications
            if (
                loanUsage === ELoanUsage.BUSINESS &&
                display === EDisplays.BASIC
            ) {
                dispatch(setDisplay(EDisplays.IDENTIFICATION));
            }
        });

        if (display === EDisplays.CAMPAIGN_LANDING) {
            if (loanUsage === ELoanUsage.BUSINESS) {
                dispatch(setDisplay(EDisplays.BUSINESS));
            } else {
                dispatch(setDisplay(EDisplays.BASIC));
            }
        }

        if (display === EDisplays.NO_LENDER_HOME) {
            if (!vehicle.isAssetTypeKnownViaParams && vehicle.isVehicleKnown) {
                dispatch(setDisplay(EDisplays.VEHICLE));
            } else {
                if (loanUsage === ELoanUsage.BUSINESS) {
                    dispatch(setDisplay(EDisplays.BUSINESS));
                } else {
                    dispatch(setDisplay(EDisplays.BASIC));
                }
            }
        }

        // Tracking
        reportConversion(EConversionType.SUBMIT_LOAN_REQUEST);
    }

    // ***** Helpers ******
    async function loginViaMobile(): Promise<EVerificationResponseStatus | void> {
        if (!isMobile(mobileNumber)) return;
        if (mobileNumber === null) return;
        if (mobileNumber === undefined) return;
        setSending(true);

        const response = await sendSmsCode(mobileNumber, notFound);

        setSending(false);
        setSent(true);
        setRateLimitingError(false);
        return response;
    }

    async function loginViaEmail(): Promise<EVerificationResponseStatus | void> {
        if (!isEmail(email)) return;
        if (email === null) return;
        if (email === undefined) return;
        setSending(true);

        const response = await sendEmailCode(email);
        setSending(false);
        setSent(true);

        return response;
    }

    // Same functionality for both loginViaMobile and loginViaEmail
    function useCode(code: EVerificationResponseStatus | void): void {
        if (code === EVerificationResponseStatus.SUCCESSFUL) {
            setSent(true);
            setRateLimitingError(false);
            return;
        }

        if (code === EVerificationResponseStatus.INCORRECT_VERIFICATION_CODE) {
            setInvalidCode(true);
            return;
        }

        if (!code) {
            setNotFound(true);
            setSent(false);
            setSending(false);
            return;
        }

        setError(true);
        return;
    }

    async function checkLoginCode(): Promise<void> {
        if (code?.length !== 6) return;

        let response: void | IVerifyCodeReturn = undefined;
        if (loginMethod === EUserLoginOption.MOBILE) {
            response = await checkLoginViaMobile();
        } else {
            response = await checkLoginViaEmail();
        }

        useVerificationCode(response?.status);
    }

    // Same functionality for both checkLoginViaMobile and checkLoginViaEmail
    function useVerificationCode(
        code: EVerificationResponseStatus | void
    ): void {
        if (code === EVerificationResponseStatus.SUCCESSFUL) {
            login();
            return;
        }

        if (code === EVerificationResponseStatus.INCORRECT_VERIFICATION_CODE) {
            setInvalidCode(true);
            return;
        }

        setError(true);
        return;
    }

    async function checkLoginViaEmail(): Promise<IVerifyCodeReturn | void> {
        if (!isEmail(email)) return;
        if (email === null) return;
        if (email === undefined) return;

        setChecking(true);

        const response = await verifyEmailCode(email, code);
        setChecking(false);

        return response;
    }

    async function checkLoginViaMobile(): Promise<IVerifyCodeReturn | void> {
        if (!isMobile(mobileNumber)) return;
        if (mobileNumber === null) return;
        if (mobileNumber === undefined) return;

        setChecking(true);

        const response = await verifySmsCode(mobileNumber, code);
        setChecking(false);
        return response;
    }

    function isReadyToSendCode(): boolean {
        if (disabled || sending || sent) return false;

        if (loginMethod === EUserLoginOption.MOBILE) {
            if (!mobileNumber || !isMobile(mobileNumber)) return false;

            if (
                smsRequestAttempts >= loggingAttempts &&
                smsLastRequestTimestamp
            ) {
                const now = Date.now();
                const timeSinceLastRequest =
                    now - (smsLastRequestTimestamp?.getTime() || 0);

                if (timeSinceLastRequest < 15 * 60 * 1000) {
                    return false; // Button will be disabled
                }
            }

            if (notFound) {
                if (firstName === undefined || firstName.trim() === '')
                    return false;

                if (signupOptions?.customerEmail) {
                    if (!customerEmail || !isEmail(customerEmail)) return false;
                }
                if (signupOptions?.zipcode) {
                    if (!postcode) return false;
                    return postcode.length === 4;
                }
            }
            return true;
        }

        if (loginMethod === EUserLoginOption.EMAIL) {
            return isEmail(email);
        }

        return false;
    }

    // ***** Render *****
    return (
        <div data-testid={testId} className='_flex flex-col'>
            {notFound && modal === EModals.LOGIN && (
                <>
                    <FirstName baseId={baseId} showErrors={false} />{' '}
                    {signupOptions?.zipcode && (
                        <PostCode baseId={baseId} showErrors={false} />
                    )}
                    {signupOptions?.customerEmail && (
                        <Email baseId={baseId} showErrors={false} />
                    )}
                </>
            )}

            <div className='_flex space-x-2'>
                <div className='py-2 mx-auto w-full max-w-md'>
                    <div className='_flex'>
                        <Button
                            id={`${baseId}-button-sendCode`}
                            onClick={handleClick}
                            type={
                                isReadyToSendCode()
                                    ? EButtonType.PRIMARY
                                    : EButtonType.PRIMARY_DISABLED
                            }
                        >
                            <p> {sent ? 'Code Sent' : 'Send Code'}</p>
                        </Button>
                    </div>
                </div>
                <Input
                    placeholder='XXXXXX'
                    showErrors={showErrors}
                    onChange={handleChange}
                    id={`${baseId}-input-verificationCode`}
                    value={code}
                    disabled={!sent && !disabled}
                    showNumpad={true}
                />
            </div>

            {invalidCode && (
                <p className='text-red-500 text-center'>Invalid Code</p>
            )}
            {sending && <p className=' text-center'>Sending...</p>}
            {rateLimitingError && (
                <p className='text-red-500 text-center'>
                    Too many attempts, please try again later
                </p>
            )}
            {error && (
                <p className='text-red-500 text-center'>
                    Error, please try again
                </p>
            )}
            {notFound && modal === EModals.LOGIN && (
                <>
                    <p className='text-red-500 text-center text-sm pb-2'>
                        Sorry, you don&apos;t have an account with us
                    </p>
                </>
            )}
            {!notFound && (
                <p className='font-bold text-xs text-textBody text-center py-2'>
                    {modal === EModals.LOGIN ? "Don't" : 'Already'} have an
                    account?
                </p>
            )}
            {checking && <p className='text-center'>Checking ...</p>}
        </div>
    );
}
